// src/marketing/HomePage.js - Template Marketplace Homepage
import React from 'react';
import { Link } from 'react-router-dom';
import {
  Check,
  ArrowRight,
  Sparkles,
  Download,
  Star,
  LayoutGrid,
  Zap,
} from 'lucide-react';

const PLATFORM_COLORS = {
  'Google Sheets': { bg: 'bg-green-100 dark:bg-green-900', text: 'text-green-700 dark:text-green-300' },
  'Excel': { bg: 'bg-emerald-100 dark:bg-emerald-900', text: 'text-emerald-700 dark:text-emerald-300' },
  'Notion': { bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-700 dark:text-gray-300' },
  'Airtable': { bg: 'bg-yellow-100 dark:bg-yellow-900', text: 'text-yellow-700 dark:text-yellow-300' },
};

const FEATURED_TEMPLATES = [
  {
    title: 'Business Budget Tracker Pro',
    description: 'Complete budget management with income tracking, expense categories, and visual dashboards.',
    platform: 'Google Sheets',
    category: 'Business & Finance',
    price: 0,
    downloads: 12400,
    rating: 4.8,
    image: 'https://placehold.co/400x250/EEF2FF/3B82F6?text=Budget+Tracker',
    slug: 'business-budget-tracker-pro',
  },
  {
    title: 'Project Management Dashboard',
    description: 'Kanban-style project tracker with timelines, team assignments, and progress charts.',
    platform: 'Notion',
    category: 'Project Management',
    price: 9.99,
    downloads: 8200,
    rating: 4.9,
    image: 'https://placehold.co/400x250/F0FDF4/22C55E?text=Project+Dashboard',
    slug: 'project-management-dashboard',
  },
  {
    title: 'Sales CRM Pipeline',
    description: 'Track leads, deals, and revenue with automated follow-up reminders and conversion analytics.',
    platform: 'Excel',
    category: 'Marketing & Sales',
    price: 14.99,
    downloads: 6800,
    rating: 4.7,
    image: 'https://placehold.co/400x250/FEF3C7/D97706?text=CRM+Pipeline',
    slug: 'sales-crm-pipeline',
  },
];

const CATEGORIES = [
  { name: 'Business & Finance', icon: '💼', count: 120, slug: 'business-finance' },
  { name: 'Project Management', icon: '📋', count: 95, slug: 'project-management' },
  { name: 'Marketing & Sales', icon: '📈', count: 78, slug: 'marketing-sales' },
  { name: 'Personal Productivity', icon: '⚡', count: 145, slug: 'personal-productivity' },
  { name: 'Education & Learning', icon: '📚', count: 62, slug: 'education-learning' },
  { name: 'Health & Fitness', icon: '💪', count: 54, slug: 'health-fitness' },
  { name: 'Real Estate', icon: '🏠', count: 38, slug: 'real-estate' },
  { name: 'HR & People', icon: '👥', count: 42, slug: 'hr-people' },
  { name: 'Inventory & Operations', icon: '📦', count: 56, slug: 'inventory-operations' },
  { name: 'Creative & Content', icon: '🎨', count: 67, slug: 'creative-content' },
];

const HomePage = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-300">
      {/* Hero Section */}
      <header className="pt-2 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="md:flex md:items-center md:justify-between">
            <div className="md:w-1/2 mb-12 md:mb-0">
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 dark:bg-blue-900/50 text-blue-600 dark:text-blue-300 text-sm font-medium mb-6">
                <Sparkles className="w-4 h-4 mr-2" />
                Now with AI Template Builder
              </div>
              <h1 className="text-4xl md:text-5xl font-bold leading-tight mb-6 dark:text-white">
                Templates for
                <br />
                <span className="text-blue-600">Everything You Track</span>
              </h1>
              <p className="text-xl text-gray-600 dark:text-gray-300 mb-8">
                Browse hundreds of ready-to-use templates for Google Sheets, Excel,
                Notion, Airtable & more. Free and premium templates to supercharge
                your productivity.
              </p>
              <div className="space-y-4 sm:space-y-0 sm:space-x-4 sm:flex sm:items-center">
                <Link
                  to="/templates"
                  className="w-full sm:w-auto flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 md:py-4 md:text-lg md:px-10"
                >
                  Browse Templates
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
                <Link
                  to="/signup"
                  className="w-full sm:w-auto flex items-center justify-center px-8 py-3 border border-gray-300 dark:border-gray-600 text-base font-medium rounded-md text-gray-700 dark:text-white bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 md:py-4 md:text-lg md:px-10"
                >
                  Start Selling
                </Link>
              </div>
              <div className="mt-6 flex items-center text-gray-500 dark:text-gray-400 text-sm">
                <Check className="h-5 w-5 text-green-500 mr-2" />
                <span>100+ free templates</span>
                <span className="mx-3">&middot;</span>
                <Check className="h-5 w-5 text-green-500 mr-2" />
                <span>AI-powered builder</span>
                <span className="mx-3">&middot;</span>
                <Check className="h-5 w-5 text-green-500 mr-2" />
                <span>Instant download</span>
              </div>
            </div>
            <div className="md:w-1/2 md:pl-8">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-200 to-purple-300 rounded-lg transform -rotate-2"></div>
                <div className="relative rounded-lg overflow-hidden shadow-xl">
                  <img
                    src="https://placehold.co/600x400/EEF2FF/3B82F6?text=Template+Marketplace"
                    alt="Template Marketplace"
                    className="w-full rounded-lg shadow-lg transition-transform duration-300 transform hover:scale-105"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Platform Logos */}
      <section className="py-12 bg-gray-50 dark:bg-gray-800 border-y border-gray-100 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-sm font-medium text-gray-500 dark:text-gray-400 mb-8 uppercase tracking-wider">
            Templates for all your favorite platforms
          </p>
          <div className="flex justify-center items-center space-x-12 flex-wrap gap-y-6">
            {[
              { name: 'Google Sheets', color: 'text-green-600' },
              { name: 'Microsoft Excel', color: 'text-emerald-600' },
              { name: 'Notion', color: 'text-gray-700 dark:text-gray-300' },
              { name: 'Airtable', color: 'text-yellow-600' },
              { name: 'Monday.com', color: 'text-red-500' },
              { name: 'Trello', color: 'text-blue-500' },
            ].map((platform) => (
              <div key={platform.name} className={`text-lg font-bold ${platform.color} opacity-60 hover:opacity-100 transition-opacity`}>
                {platform.name}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Templates */}
      <section className="py-16 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-10">
            <div>
              <h2 className="text-3xl font-bold dark:text-white">Featured Templates</h2>
              <p className="mt-2 text-gray-600 dark:text-gray-400">Hand-picked templates to get you started</p>
            </div>
            <Link
              to="/templates"
              className="hidden sm:inline-flex items-center text-blue-600 hover:text-blue-700 font-medium"
            >
              View all templates
              <ArrowRight className="ml-1 h-4 w-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {FEATURED_TEMPLATES.map((template) => {
              const platformStyle = PLATFORM_COLORS[template.platform] || PLATFORM_COLORS['Google Sheets'];
              return (
                <Link
                  key={template.slug}
                  to={`/templates/${template.slug}`}
                  className="group bg-white dark:bg-gray-800 rounded-xl shadow-sm hover:shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden transition-all duration-300"
                >
                  <div className="relative">
                    <img
                      src={template.image}
                      alt={template.title}
                      className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute top-3 left-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${platformStyle.bg} ${platformStyle.text}`}>
                        {template.platform}
                      </span>
                    </div>
                    <div className="absolute top-3 right-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                        template.price === 0
                          ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                          : 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                      }`}>
                        {template.price === 0 ? 'FREE' : `$${template.price}`}
                      </span>
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="font-semibold text-lg text-gray-900 dark:text-white group-hover:text-blue-600 transition-colors">
                      {template.title}
                    </h3>
                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                      {template.description}
                    </p>
                    <div className="mt-4 flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
                      <div className="flex items-center space-x-3">
                        <span className="flex items-center">
                          <Star className="w-4 h-4 text-yellow-400 mr-1 fill-yellow-400" />
                          {template.rating}
                        </span>
                        <span className="flex items-center">
                          <Download className="w-4 h-4 mr-1" />
                          {template.downloads.toLocaleString()}
                        </span>
                      </div>
                      <span className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                        {template.category}
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>

          <div className="mt-8 text-center sm:hidden">
            <Link
              to="/templates"
              className="inline-flex items-center text-blue-600 font-medium"
            >
              View all templates
              <ArrowRight className="ml-1 h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Browse by Category */}
      <section className="py-16 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold dark:text-white">Browse by Category</h2>
            <p className="mt-3 text-lg text-gray-600 dark:text-gray-400">
              Find templates for every use case
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {CATEGORIES.map((category) => (
              <Link
                key={category.slug}
                to={`/templates?category=${category.slug}`}
                className="bg-white dark:bg-gray-900 p-5 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600 hover:shadow-md transition-all text-center group"
              >
                <div className="text-3xl mb-3">{category.icon}</div>
                <h3 className="font-medium text-gray-900 dark:text-white text-sm group-hover:text-blue-600 transition-colors">
                  {category.name}
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {category.count} templates
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why TrackerSheets */}
      <section className="py-16 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold dark:text-white">
              Why TrackerSheets?
            </h2>
            <p className="mt-4 text-xl text-gray-600 dark:text-gray-400">
              The template marketplace built for creators and teams
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            {[
              {
                icon: Zap,
                title: 'Instant Access',
                description:
                  'Download and start using templates immediately. No setup, no waiting. Just open and go.',
              },
              {
                icon: Sparkles,
                title: 'AI-Powered Builder',
                description:
                  'Describe what you need and our AI builds a custom template for you in seconds. No spreadsheet skills required.',
              },
              {
                icon: LayoutGrid,
                title: 'Multi-Platform',
                description:
                  'Templates for Google Sheets, Excel, Notion, Airtable, and more. Use what you already know.',
              },
            ].map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className="relative p-6 bg-gray-50 dark:bg-gray-800 rounded-lg hover:shadow-md transition-shadow"
                >
                  <div className="absolute top-6 left-6 bg-blue-100 dark:bg-blue-900 rounded-full p-3">
                    <Icon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <h3 className="mt-16 text-xl font-medium dark:text-white">
                    {feature.title}
                  </h3>
                  <p className="mt-2 text-base text-gray-500 dark:text-gray-400">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-blue-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold dark:text-white">
              How It Works
            </h2>
            <p className="mt-4 text-xl text-gray-600 dark:text-gray-400">
              Get started in under 2 minutes
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-4">
            {[
              {
                step: '1',
                title: 'Browse or Build',
                description: 'Search our marketplace or describe what you need to our AI builder.',
              },
              {
                step: '2',
                title: 'Preview',
                description: 'See exactly what you get with full template previews and screenshots.',
              },
              {
                step: '3',
                title: 'Download',
                description: 'Get instant access. Free templates download immediately, premium with one click.',
              },
              {
                step: '4',
                title: 'Customize',
                description: 'Make it yours. Every template is fully editable to fit your workflow.',
              },
            ].map((step, index) => (
              <div key={index} className="relative">
                <div className="h-12 w-12 rounded-full bg-blue-600 text-white flex items-center justify-center text-xl font-bold mb-4 mx-auto">
                  {step.step}
                </div>
                <h3 className="text-xl text-center font-medium dark:text-white">
                  {step.title}
                </h3>
                <p className="mt-2 text-base text-center text-gray-500 dark:text-gray-400">
                  {step.description}
                </p>
                {index < 3 && (
                  <div className="hidden md:block absolute top-6 left-full w-8">
                    <ArrowRight className="h-6 w-6 text-gray-300" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Creator CTA */}
      <section className="py-16 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 md:p-12 text-white">
            <div className="md:flex md:items-center md:justify-between">
              <div className="md:w-2/3">
                <h2 className="text-3xl font-bold mb-4">Sell Your Templates</h2>
                <p className="text-lg text-blue-100 mb-6 md:mb-0">
                  Join our marketplace as a creator. Build templates, set your prices,
                  and earn money from your spreadsheet expertise. Our AI builder makes
                  it easy to create professional templates fast.
                </p>
              </div>
              <div className="md:w-1/3 md:text-right">
                <Link
                  to="/signup"
                  className="inline-flex items-center px-8 py-4 bg-white text-blue-600 font-bold rounded-lg hover:bg-blue-50 transition-colors text-lg"
                >
                  Become a Creator
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-3 lg:gap-8">
            <div>
              <h2 className="text-3xl font-bold dark:text-white">
                Loved by Teams & Creators
              </h2>
              <p className="mt-4 text-lg text-gray-500 dark:text-gray-400">
                See how people use TrackerSheets templates to work smarter.
              </p>
              <div className="mt-6 flex items-center space-x-4">
                <div className="flex items-center">
                  <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                  <span className="ml-1 font-bold text-gray-900 dark:text-white">4.8</span>
                </div>
                <span className="text-gray-500 dark:text-gray-400">from 2,400+ reviews</span>
              </div>
            </div>
            <div className="mt-12 lg:mt-0 lg:col-span-2">
              <div className="space-y-6">
                {[
                  {
                    quote:
                      "The budget tracker template saved me hours of setup. I just opened it and started tracking. Best $0 I ever spent!",
                    author: 'Sarah K.',
                    role: 'Small Business Owner',
                  },
                  {
                    quote:
                      "I've made over $3,000 selling my project management templates on TrackerSheets. The AI builder helped me create 10x faster.",
                    author: 'Marcus Chen',
                    role: 'Template Creator',
                  },
                ].map((testimonial, index) => (
                  <div
                    key={index}
                    className="bg-white dark:bg-gray-900 p-6 rounded-lg shadow-sm"
                  >
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <svg
                          className="h-8 w-8 text-blue-400"
                          fill="currentColor"
                          viewBox="0 0 32 32"
                        >
                          <path d="M9.352 4C4.456 7.456 1 13.12 1 19.36c0 5.088 3.072 8.064 6.624 8.064 3.36 0 5.856-2.688 5.856-5.856 0-3.168-2.208-5.472-5.088-5.472-.576 0-1.344.096-1.536.192.48-3.264 3.552-7.104 6.624-9.024L9.352 4zm16.512 0c-4.8 3.456-8.256 9.12-8.256 15.36 0 5.088 3.072 8.064 6.624 8.064 3.264 0 5.856-2.688 5.856-5.856 0-3.168-2.304-5.472-5.184-5.472-.576 0-1.248.096-1.44.192.48-3.264 3.456-7.104 6.528-9.024L25.864 4z" />
                        </svg>
                      </div>
                      <div className="ml-4">
                        <p className="text-base text-gray-800 dark:text-gray-200">
                          {testimonial.quote}
                        </p>
                        <div className="mt-2">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {testimonial.author}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {testimonial.role}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
